'use strict';

var jsxRuntime = require('react/jsx-runtime');
var react = require('react');
var tailwindMerge = require('tailwind-merge');
var Typography = require('../../Base/Typography/Typography.js');
var Box = require('../../Base/Layouts/Box/Box.js');
require('../../Base/Layouts/FullBleed/FullBleed.js');
require('../../DataDisplays/Avatar/Avatar.js');
var CountBadge = require('../../DataDisplays/CountBadge/CountBadge.js');
require('../../DataDisplays/Label/Label.js');
var SystemIcon = require('../../DataDisplays/SystemIcon/SystemIcon.js');

/**
 * 필터 선택 및 상태를 표시하는 칩 컴포넌트입니다.
 *
 * @component
 * @param {Object} props
 * @param {string} props.text - 칩에 표시될 텍스트
 * @param {SystemIconName} [props.leadingIcon] - 텍스트 앞에 표시될 아이콘
 * @param {SystemIconName} [props.trailingIcon] - 텍스트 뒤에 표시될 아이콘
 * @param {string} [props.badgeCount] - 선택된 항목 수를 표시하는 뱃지 텍스트
 * @param {string} [props.className] - 추가적인 스타일 클래스
 * @param {boolean} [props.isOpen=false] - 드롭다운 메뉴의 열림 상태
 * @param {() => void} [props.onClick] - 클릭 이벤트 핸들러
 * @param {React.ReactNode} [props.children] - 드롭다운 메뉴의 내부 컨텐츠
 *
 * @example
 * // 기본 사용
 * <FilterChip text="카테고리" />
 *
 * // 아이콘과 함께 사용
 * <FilterChip
 *   text="필터"
 *   leadingIcon="filter"
 *   trailingIcon="chevron-down"
 * />
 *
 * // 선택된 항목 수 표시
 * <FilterChip
 *   text="상태"
 *   badgeCount="3"
 *   leadingIcon="status"
 * />
 *
 * // 드롭다운 메뉴와 함께 사용
 * <FilterChip
 *   text="정렬"
 *   isOpen={true}
 *   leadingIcon="sort"
 * >
 *   <DropdownMenu>
 *     <MenuItem>최신순</MenuItem>
 *     <MenuItem>오래된순</MenuItem>
 *   </DropdownMenu>
 * </FilterChip>
 */ const FilterChip = react.forwardRef(({ leadingIcon, trailingIcon, text, children, onClick, isOpen = false, badgeCount, className }, ref)=>{
    const isSelected = badgeCount && Number(badgeCount) > 0;
    const isFocused = isOpen || isSelected;
    return jsxRuntime.jsxs(Box, {
        ref: ref,
        "data-testid": "design-system-filter-chip--outer-container",
        className: tailwindMerge.twMerge('relative w-fit', className),
        children: [
            jsxRuntime.jsxs(Box, {
                "data-testid": "design-system-filter-chip",
                className: tailwindMerge.twMerge('relative flex w-full cursor-pointer select-none items-center gap-1 rounded-full border border-w-gray-200 bg-w-white px-[15px] py-1.5 hover:border-primary', isOpen && 'border-primary', isSelected && 'border-2 border-primary bg-primary-10 px-[14px] py-[5px]'),
                onClick: onClick,
                children: [
                    leadingIcon && jsxRuntime.jsx(SystemIcon, {
                        testId: "design-system-filter-chip--leading-icon",
                        name: leadingIcon,
                        className: tailwindMerge.twMerge('text-w-gray-900', isFocused && 'text-primary')
                    }),
                    jsxRuntime.jsx(Typography, {
                        variant: "body1",
                        className: tailwindMerge.twMerge('text-w-gray-900', isFocused && 'text-primary'),
                        children: text
                    }),
                    trailingIcon && jsxRuntime.jsx(SystemIcon, {
                        testId: "design-system-filter-chip--trailing-icon",
                        name: trailingIcon,
                        className: tailwindMerge.twMerge('text-w-gray-400', isFocused && 'text-primary')
                    }),
                    isSelected && jsxRuntime.jsx(CountBadge, {
                        text: badgeCount,
                        className: "-right-1 -top-1"
                    })
                ]
            }),
            isOpen && jsxRuntime.jsx(Box, {
                "data-testid": "design-system-filter-chip--list",
                className: "absolute -bottom-2 left-0 z-40 translate-y-full transform transition-all",
                children: children
            })
        ]
    });
});
FilterChip.displayName = 'FilterChip';

module.exports = FilterChip;
